import React, { useMemo, useState } from 'react';
import CommonLayout from './CommonLayout';
import { Calendar, Event, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useUserSessionStore } from 'stores/UserSession.store';
import { UserTypeEnum } from '../../@core/enums/UserType.enum';
import { uniqBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import Label from '../../components/Label/Label';
import { useReservations } from '../../hooks/collections/useReservations';
import { IReservation } from '../../@core/interfaces/IReservation';
import { Collections } from '../../@core/constants/FirebasePaths';
import { IQuery, useFirebaseData } from '../../@shared/hooks/useFirebaseData';

const localizer = momentLocalizer(moment);
const TypedCalendar = Calendar as unknown as React.FC<any>;

const AccountAgenda = () => {
  const navigate = useNavigate();
  const user = useUserSessionStore((state) => state.user);
  const db = useFirebaseData({ collectionPath: null, loadData: false });
  const { reservations } = useReservations({
    loadData: true,
    queries: [
      ...(user?.role === UserTypeEnum.COMPANY ? [{
        type: 'where',
        field: 'service.company.id',
        operator: '==',
        value: user.id
      }] : []),
      ...(user?.role === UserTypeEnum.PROVIDER ? [{
        type: 'where',
        field: 'service.details.provider.id',
        operator: '==',
        value: user.id
      }] : []),
      ...(user?.role === UserTypeEnum.USER ? [{
        type: 'where',
        field: 'createdBy',
        operator: '==',
        value: db.getReference(`${Collections.users._path}/${user.id}`)
      }] : []),
      { type: 'orderBy', field: 'createdAt', direction: 'desc' }
    ] as IQuery[]
  });

  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedProvider, setSelectedProvider] = useState<string>('all');

  const locations = useMemo(() => {
    const reservationsWithLocation = (reservations || []).filter((reservation: IReservation) => reservation.service?.details?.location?.name);
    const uniqueLocations = uniqBy(reservationsWithLocation.map((reservation: IReservation) => reservation.service.details.location), 'id');
    return uniqueLocations.map(location => location.name);
  }, [reservations]);

  const providers = useMemo(() => {
    const reservationsWithProvider = (reservations || []).filter((reservation: IReservation) => reservation.service?.details?.provider?.name);
    const uniqueProviders = uniqBy(reservationsWithProvider.map((reservation: IReservation) => reservation.service.details.provider), 'id');
    return uniqueProviders.map(provider => ({ id: provider.id, name: provider.name }));
  }, [reservations]);

  const filteredReservationsWithFilters = useMemo(() => {
    if (!reservations || reservations.length === 0) return [];

    return reservations.filter((reservation: IReservation) => {
      const passesLocationFilter = selectedLocation === 'all' || reservation.service?.details?.location?.name === selectedLocation;
      const passesProviderFilter = selectedProvider === 'all' || reservation.service?.details?.provider?.id === selectedProvider;
      return passesLocationFilter && passesProviderFilter;
    });
  }, [reservations, selectedLocation, selectedProvider]);

  const events = useMemo(() => {
    return filteredReservationsWithFilters.map((reservation: IReservation) => {
      const startDateTime = moment(
        `${reservation.day} ${reservation.hour}`,
        'YYYY-MM-DD HH:mm'
      ).toDate();
      const endDateTime = moment(startDateTime).add(1, 'hour').toDate();
      return {
        id: reservation.id,
        title: reservation.service.title,
        start: startDateTime,
        end: endDateTime,
        resource: reservation
      } as Event;
    });
  }, [filteredReservationsWithFilters]);

  const renderSection1 = () => {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="flex flex-col md:flex-row gap-5 items-center justify-between">
          <h2 className="text-3xl font-semibold">Agenda</h2>

          {user?.role === UserTypeEnum.COMPANY && (
            <div className="flex items-center space-x-2">
              <div>
                <Label>Location</Label>
                <select
                  className="mt-1.5 pe-10 h-9 px-4 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700"
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                >
                  <option value="all">All Locations</option>
                  {locations.map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Provider</Label>
                <select
                  className="mt-1.5 pe-10 h-9 px-4 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700"
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                >
                  <option value="all">All Providers</option>
                  {providers.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        <div className="h-[600px] bg-white dark:bg-neutral-800 p-4 rounded-lg shadow">
          <TypedCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            views={['month', 'week', 'day']}
            defaultView="month"
            tooltipAccessor={(event: Event) => event.title}
            onSelectEvent={(event: Event) => navigate(`/order-detail/${event.resource.order.id}`)}
          />
        </div>
      </div>
    );
  };

  return (
    <CommonLayout>{renderSection1()}</CommonLayout>
  );
};

export default AccountAgenda;
